// @ts-nocheck
// TODO: Fix function argument count error - shouldSupportCurrencyCheck expects 3 arguments but got 2
// Error: TS2554: Expected 3 arguments, but got 2
// File: src/util/transformers/common/backendConverter.ts
// Line: 81 (shouldSupportCurrencyCheck(businessId, workflow) call)
// Resolution needed: Add missing third argument to shouldSupportCurrencyCheck function call
/**
 * Common utilities to convert front-end data to back-end data
 */
import { shouldSupportCurrencyCheck } from 'util/BusinessUtils';

import { RequestPropertyIds } from '../../../data/BackendEntityConstants';
import { DocumentEntry, QuoteEntry } from '../../../interfaces';
import {
  Document,
  Property,
  RFXDocumentType,
  RFXIdentifier,
  RFXRevisionedId,
  Supplier
} from '../../../interfaces/RFXBackendModel';
import { RFXRequestDocuments, SupplierEntry, TrackingInformation } from '../../../interfaces/RFXRequestForm';
import { createProperty } from '../../RFXUtils';

/**
 * Generate revisioned ID
 */
export function generateRevisionedId(id: string, revision: number = 1): RFXRevisionedId {
  return {
    id: { value: id },
    revision
  };
}

/**
 * Convert region strings to a property
 */
export function convertRegions(regions: string[]): Property {
  return {
    id: { value: RequestPropertyIds.Regions },
    value: {
      valueString: { value: JSON.stringify(regions) },
      dataType: { value: 'String' }
    }
  };
}

/**
 * Convert stie strings to a property
 */
export function convertSites(sites: string[]): Property {
  return {
    id: { value: RequestPropertyIds.Sites },
    value: {
      valueString: { value: JSON.stringify(sites) },
      dataType: { value: 'String' }
    }
  };
}

/**
 * Convert category strings to a property
 */
export function convertCategories(categories: string[]): Property {
  return {
    id: { value: RequestPropertyIds.Categories },
    value: {
      valueString: { value: JSON.stringify(categories) },
      dataType: { value: 'String' }
    }
  };
}

/**
 * Convert supplier entries UI model to backend model
 */
export function convertSupplierEntries(
  supplierEntries: SupplierEntry[],
  businessId: string,
  workflow: string
): Supplier[] {
  const suppliers: Supplier[] = [];
  if (!Array.isArray(supplierEntries) || supplierEntries.length === 0) {
    return suppliers;
  }

  const shouldSupportCurrencyCheckforSupplier: boolean = shouldSupportCurrencyCheck(businessId, workflow);
  supplierEntries.forEach((supplierEntry) => {
    const supplier: Supplier = {
      supplierId: { value: supplierEntry.supplierId },
      supplierName: { value: supplierEntry.supplierName ?? '' },
      contactId: { value: supplierEntry.contactList },
      // TODO: confirm what's contactSource from backend model
      contactSource: supplierEntry.contactSource,
      countryCode: supplierEntry.countryCode,
      incoterm: supplierEntry.incoterm,
      qualificationStatus: supplierEntry.qualificationStatus ? supplierEntry.qualificationStatus : undefined,
      vendorServiceCurrencies: shouldSupportCurrencyCheckforSupplier
        ? supplierEntry.vendorServiceCurrencies
        : undefined,
      selectedCurrencyValid: shouldSupportCurrencyCheckforSupplier ? supplierEntry.selectedCurrencyValid : undefined
    };
    suppliers.push(supplier);
  });

  return suppliers;
}

/**
 * Convert one document entry UI model to backend model
 */
export function convertDocumentEntry(documentEntry: DocumentEntry, supplierIds: RFXIdentifier[] = []): Document {
  if (!documentEntry.type.value) {
    throw new Error(`Document with ID [${documentEntry.attachmentId}] has no type.`);
  }

  const documentType = documentEntry.type.value;

  return {
    documentType,
    supplierIds,
    attachment: {
      externalId: documentEntry.attachmentId,
      version: '' + documentEntry.version,
      filename: documentEntry.attachmentName,
      size: '0',
      format: documentEntry.format,
      description: documentEntry.description,
      stagingS3Uri: documentEntry.stagingS3Uri,
      status: 'CREATED',
      createdDate: {
        value: documentEntry.createdDate,
        format: 'ISO_INSTANT'
      }
    }
  };
}

/**
 * Convert documents UI model for RFX request to backend model
 */
export function convertRFXRequestDocumentsToBackendModel({
  documentEntries,
  supportDocuments
}: RFXRequestDocuments): Document[] {
  let resultDocuments: Document[] = [];
  if (documentEntries) {
    documentEntries.forEach((documentEntry) => {
      resultDocuments.push(convertDocumentEntry(documentEntry));
    });
  }
  if (supportDocuments) {
    supportDocuments.forEach((documentEntry) => {
      resultDocuments.push(convertDocumentEntry(documentEntry));
    });
  }
  return resultDocuments;
}

/**
 * Convert documents UI model for RFX response to backend model
 */
export function convertDocumentsToBackendModel(
  documentEntries: DocumentEntry[],
  supplierIds: RFXIdentifier[] = []
): Document[] {
  const documents: Document[] = [];
  documentEntries.forEach((documentEntry) => {
    documents.push(convertDocumentEntry(documentEntry, supplierIds));
  });
  return documents;
}

/**
 * Convert tracking info UI model to properties
 */
export function convertTrackingInfoToProperties(trackingInfo: TrackingInformation | undefined): Property[] {
  const properties: Property[] = [];
  if (trackingInfo === undefined) {
    return properties;
  }
  if (trackingInfo.publishedAt !== undefined) {
    properties.push(createProperty(RequestPropertyIds.PublishedAt, trackingInfo.publishedAt.toString(), 'Long'));
  }
  if (trackingInfo.awardedAt !== undefined) {
    properties.push(createProperty(RequestPropertyIds.AwardedAt, trackingInfo.awardedAt.toString(), 'Long'));
  }

  return properties;
}

/**
 * Convert first quote entry to backend document.
 *
 * Pre-condition: Quote entries are sorted before calling this, so that the first entry is the latest.
 *
 * @param quoteEntries UI model of quote entries
 * @returns converted first quote entry. If quote entries is empty or invalid, return null
 */
export function convertQuoteEntriesToDocument(quoteEntries: QuoteEntry[]): Document | null {
  if (!Array.isArray(quoteEntries) || quoteEntries.length === 0) {
    return null;
  }

  const {
    attachmentId,
    version,
    fileName,
    fileSize,
    format,
    description,
    stagingS3Uri,
    uploadStatus,
    uploadDate,
    lastModifiedDate
  } = quoteEntries[0];

  const document: Document = {
    documentType: RFXDocumentType.QUOTE,
    supplierIds: [],
    attachment: {
      externalId: attachmentId,
      version: '' + version,
      filename: fileName,
      size: fileSize,
      format: format,
      description: description,
      stagingS3Uri: stagingS3Uri,
      status: uploadStatus,
      createdDate: {
        value: uploadDate,
        format: 'ISO_INSTANT'
      }
    }
  };
  if (lastModifiedDate) {
    document.attachment.lastModifiedDate = {
      value: lastModifiedDate,
      format: 'ISO_INSTANT'
    };
  }
  return document;
}
